import * as React from 'react';
import { ThemeContext } from 'styled-components';
import camelCase from 'lodash/camelCase';
import groupBy from 'lodash/groupBy';
import head from 'lodash/head';

import InputControl from '../InputControl/InputControl';
import { IInputFieldProps } from '../TextInput/InputTextField';
import { Box } from '../../Layout/Box';
import ScreenReaderOnly from '../../Accessibility/ScreenReaderOnlyStyles';
import { IInputProps } from '../common';
import {
  ARROW_DOWN,
  ARROW_DOWN_ATL,
  ARROW_UP,
  ARROW_UP_ATL,
  ENTERKEY,
  ENTERKEY_ATL,
  ESCAPE,
  ESCAPE_ATL,
  TAB,
  useEventListener,
  VALID_KEYS
} from '../../Utils';
import TextInput from '../TextInput/Input';
import { handleEscape, handleKeydown } from '../../Utils/events/events';

import {
  OptionList,
  OptionListItem,
  OptionListItemHeader,
  OptionListItemNoResults
} from './AutoCompleteStyles';

const VALID_KEY_CODES: VALID_KEYS[] = [
  ARROW_DOWN,
  ARROW_DOWN_ATL,
  ARROW_UP,
  ARROW_UP_ATL,
  ENTERKEY,
  ENTERKEY_ATL,
  ESCAPE,
  ESCAPE_ATL,
  TAB,
];

const VALID_ARROW_KEYS: VALID_KEYS[] = [
  ARROW_DOWN,
  ARROW_DOWN_ATL,
  ARROW_UP,
  ARROW_UP_ATL,
];

const VALID_ENTER_KEYS: VALID_KEYS[] = [
  ENTERKEY,
  ENTERKEY_ATL,
];

const DEFAULT_ACTIVE_OPTION = { option: '', id: '' };

const INIT_STATE: IAutoCompleteState = {
  options: [],
  filteredOptions: [],
  activeOption: DEFAULT_ACTIVE_OPTION,
  isOpen: false,
};

enum Types {
  Filter = 'filter',
  Options = 'options',
  ArrowPress = 'arrowKeyPress',
  OpenOptionList = 'open',
  CloseOptionList = 'close',
  SelectFirstOption = 'firstOption',
  ResetActiveOption = 'restActiveOption',
}

// tslint:disable-next-line: no-any
export type SelectorFunction = (val: any) => string;

interface IInputAutoCompleteProps extends IInputFieldProps, IInputProps {
  options: Object[];
  value: string;
  name: string;
  getOptionLabel: SelectorFunction;
  groupOptionsBy?: SelectorFunction;
  onChange (value: string): void;
}

interface IActiveOption {
  option: Object;
  id: string;
}

interface IAutoCompleteState {
  options: Object[];
  filteredOptions: Object[];
  activeOption: IActiveOption | null;
  isOpen: boolean;
  maxWidth?: string;
}

// tslint:disable no-reserved-keywords
type Actions =
  | { type: Types.Filter; payload: string }
  | { type: Types.Options; payload: Object[] }
  | { type: Types.ArrowPress; payload: string }
  | { type: Types.ResetActiveOption; payload: IActiveOption }
  | { type: Types.OpenOptionList; payload: true }
  | { type: Types.CloseOptionList; payload: false };
// tslint:enable no-reserved-keywords

const getOptionId = (option: string, name: string): string => camelCase(`${name}${option}Id`);

const setActiveOption = (
  filteredOptions: Object[],
  activeOption: IActiveOption,
  key: string,
  getOptionLabel: SelectorFunction,
  name: string,
): IActiveOption => {
  const startingOption = filteredOptions[0];
  const endingOption = filteredOptions[filteredOptions.length - 1];
  const index = !activeOption.option ? 0 : filteredOptions.indexOf(activeOption.option);
  let option = filteredOptions[index];
  let optionLabel = getOptionLabel(option);

  if (!activeOption.option) {
    return { option, id: getOptionId(optionLabel, name) };
  }

  if (key === ARROW_DOWN || key === ARROW_DOWN_ATL) {
    option = index === filteredOptions.length - 1 ? startingOption : filteredOptions[index + 1];
    optionLabel = getOptionLabel(option);

    return { option, id: getOptionId(optionLabel, name) };
  } else { // up arrow
    option = index === 0 ? endingOption : filteredOptions[index - 1];
    optionLabel = getOptionLabel(option);

    return { option, id: getOptionId(optionLabel, name) };
  }
};

const reducer = (
  getOptionLabel: SelectorFunction, name: string, groupOptionsBy?: SelectorFunction
) => (
  state: IAutoCompleteState, actions: Actions
) => {
  switch (actions.type) {
    case Types.Options:
      let payload: Object[];

      // this normalizes the grouped options to allow keyboard navigation through the dropdown
      if (groupOptionsBy) {
        const filteredGrouped = groupBy(actions.payload, groupOptionsBy);

        payload = Object.entries(filteredGrouped)
          .reduce((prev: Object[], [_, options]) => [...prev, ...options], []);
      } else {
        payload = actions.payload;
      }

      return { ...state, options: payload, filteredOptions: payload };

    case Types.Filter:
      const normalizedInputValue = actions.payload.toLowerCase();
      const filterByKeyword = (option: Object) => getOptionLabel(option)
        .toLowerCase()
        .includes(normalizedInputValue);
      const filteredOptions = state.options.filter(filterByKeyword);
      const firstOption = head(filteredOptions);

      const firstActiveOption = firstOption
        ? { option: firstOption, id: getOptionId(getOptionLabel(firstOption), name) }
        : null;

      return {
        ...state,
        filteredOptions,
        activeOption: firstActiveOption,
      };

    case Types.ArrowPress:
      const activeOption =
        state.activeOption && setActiveOption(state.filteredOptions, state.activeOption, actions.payload, getOptionLabel, name);

      return { ...state, activeOption };

    case Types.OpenOptionList:
      return { ...state, isOpen: actions.payload };

    case Types.CloseOptionList:
      return { ...state, isOpen: actions.payload, activeOption: DEFAULT_ACTIVE_OPTION };

    case Types.ResetActiveOption:
      return { ...state, activeOption: actions.payload };

    default:
      return state;
  }
};

// exporting here is a work around for this issue https://github.com/microsoft/tslint-microsoft-contrib/issues/667
// tslint:disable-next-line:max-func-body-length
export const InputAutoCompleteField: React.FC<IInputAutoCompleteProps> = ({
    errorMsgs,
    helperText,
    inputProps,
    label,
    tooltipProps,
    onChange,
    value,
    getOptionLabel,
    groupOptionsBy,
    options,
    name,
    maxWidth,
}) => {
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const isDisabled = !!(inputProps && inputProps.disabled);
  const isReadOnly = !!(inputProps && inputProps.readOnly);
  const isOptional: boolean = !!(inputProps && inputProps.required === false);
  const inputId: string = camelCase(`${label}${name}`);
  const instructionId = camelCase(`${inputId}Instruction`);
  const ariaControls = `${name}-results`;
  const { themeTwo: { form: { select: { icon } }}} = React.useContext(ThemeContext);
  const [ inputValue, setInputValue ] = React.useState<string>('');
  const [ { filteredOptions, activeOption, isOpen }, dispatch ] =
    React.useReducer(reducer(getOptionLabel, name, groupOptionsBy), INIT_STATE);

  const enterkeyCallback = (e: React.KeyboardEvent | Event) => {
    const event = e as React.KeyboardEvent;
    if (!!activeOption) {
      event.preventDefault();
      event.stopPropagation();
      setInputValue(getOptionLabel(activeOption.option));
      onChange(getOptionLabel(activeOption.option));
      dispatch({ type: Types.CloseOptionList, payload: false });
    }
  };
  const escapeCallback = () => {
    dispatch({ type: Types.CloseOptionList, payload: false });
  };
  const arrowCallback = (e: React.KeyboardEvent | Event) => {
    const event = e as React.KeyboardEvent;
    event.preventDefault();
    event.stopPropagation();
    dispatch({ type: Types.ArrowPress, payload: event.key});
  };

  const handleArrows = handleKeydown(VALID_ARROW_KEYS);
  const handleEnter = handleKeydown(VALID_ENTER_KEYS);

  const handleArrowKeysPress = handleArrows(arrowCallback);
  const handleEscapeKeysPress = handleEscape(escapeCallback);
  const handleEnterKeysPress = handleEnter(enterkeyCallback);

  const handleKeyboard = (event: Event) => {
    const e = event as KeyboardEvent;
    if (listRef && listRef.current || isOpen || VALID_KEY_CODES.includes(e.key)) {
      handleArrowKeysPress(e);
      handleEnterKeysPress(e);
      handleEscapeKeysPress(e);
    }
  };

  useEventListener('keydown', handleKeyboard);

  // tslint:disable no-void-expression
  React.useEffect(() => (dispatch({ type: Types.Options, payload: options })), []);
  React.useEffect(() => (setInputValue(value)), [value]);
  // tslint:enable no-void-expression

  const handleOnChange: React.ChangeEventHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (!isOpen) {
      dispatch({ type: Types.OpenOptionList, payload: true });
    }

    setInputValue(e.target.value);
    dispatch({ type: Types.Filter, payload: e.target.value });

    if (!e.target.value.length) {
      dispatch({ type: Types.ResetActiveOption, payload: DEFAULT_ACTIVE_OPTION });
    }

    if (inputProps && inputProps.onChange) { inputProps.onChange(e); }
  };

  const handleInputFocus = () => dispatch({ type: Types.OpenOptionList, payload: true });

  const handleMouseDown = (option: Object) => () => {
    onChange(getOptionLabel(option));
    setInputValue(getOptionLabel(option));
    dispatch({ type: Types.CloseOptionList, payload: false });
  };

  const handleOnBlur = () => {
    onChange(inputValue);
    setInputValue(inputValue);
    dispatch({ type: Types.CloseOptionList, payload: false });
  };

  return (
    <InputControl
      errorMsgs={errorMsgs}
      helperText={helperText}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isOptional={isOptional}
      inputProps={inputProps}
      label={label}
      tooltipProps={tooltipProps}
      maxWidth={maxWidth}
    >
      <Box display='block' position='relative'>
        <TextInput
          ref={inputRef}
          id={inputId}
          inputProps={{
            ...inputProps,
            'aria-autocomplete': 'list',
            autoComplete: 'off',
            'aria-controls': ariaControls,
            'aria-describedby': instructionId,
            'aria-expanded': isOpen,
            'aria-haspopup': 'listbox',
            'aria-activedescendant': activeOption && activeOption.id && isOpen ? activeOption.id : undefined,
            type: 'text',
            onChange: handleOnChange,
            onBlur: handleOnBlur,
            onFocus: handleInputFocus,
            value: inputValue,
          }}
          iconProps={{
            size: icon.size,
            icon: icon.type,
            position: 'right',
          }}
        />
        <Box id={instructionId} display='none'>Begin typing to search, use arrow keys to navigate, Press Enter to select</Box>
        <ScreenReaderOnly aria-live='assertive'>{filteredOptions && filteredOptions.length} results found</ScreenReaderOnly>

        {isOpen && filteredOptions &&
          <OptionList
            ref={listRef}
            id={ariaControls}
            role='listbox'
            aria-labelledby={inputId}
            maxWidth={maxWidth}
          >
            {/* if no results */}
            {!filteredOptions.length && <OptionListItemNoResults>No results found</OptionListItemNoResults>}

            {groupOptionsBy ? (
              Object.entries(groupBy(filteredOptions, groupOptionsBy))
              .map(([groupLabel, labelOptions], idx) => (
                <React.Fragment key={groupLabel}>
                  <OptionListItemHeader>{groupLabel}</OptionListItemHeader>

                  {labelOptions.map(opt => {
                    const optionLabel = getOptionLabel(opt);
                    const optionId = getOptionId(optionLabel, name);

                    return (
                      // tslint:disable-next-line:react-a11y-role-has-required-aria-props
                      <OptionListItem
                        isActive={activeOption ? optionLabel === getOptionLabel(activeOption.option) : undefined}
                        key={`${groupLabel}${optionId}${idx}`}
                        role='option'
                        id={optionId}
                        onMouseDown={handleMouseDown(opt)}
                      >
                        {getOptionLabel(opt)}
                      </OptionListItem>
                    );
                  })}
                </React.Fragment>
              ))
            ) : (
              filteredOptions.map(opt => {
                const optionLabel = getOptionLabel(opt);
                const optionId = getOptionId(optionLabel, name);

                return (
                  // tslint:disable-next-line:react-a11y-role-has-required-aria-props
                  <OptionListItem
                    isActive={activeOption ? optionLabel === getOptionLabel(activeOption.option) : undefined}
                    key={optionId}
                    role='option'
                    id={optionId}
                    onMouseDown={handleMouseDown(opt)}

                  >
                    {optionLabel}
                  </OptionListItem>
                );
              })
            )}
          </OptionList>
        }
      </Box>
    </InputControl>
  );
};

export default InputAutoCompleteField;
